import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, CardBody, FormGroup, Input, Label } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import TextareaAutosize from "react-textarea-autosize";
import { FormControlLabel, Switch } from "@mui/material";
import styles from "../../styles.scss";
import SectionLabel from "../../common/sectionLabel";
import TooltipColorPicker from "../../common/tooltipColorPicker";
import classNames from "classnames";
import { LeftSidebarVariablesProp } from "../../common/types/variables";
import { Project } from "../../../../index";

const GivingBodySection = ({ variables }: LeftSidebarVariablesProp) => {
  const [headersColor, setHeadersColor] = variables["headersAndAmountColor"];
  const [inactiveTabColor, setInactiveTabColor] = variables["inactiveTabColor"];

  const [oneTimeMessageBody, setOneTimeMessageBody] = variables[
    "oneTimeMessageText"
  ];
  const [monthlyMessageBody, setMonthlyMessageBody] = variables[
    "monthlyMessageText"
  ];
  const fetchGivingBodyOneTimeMessageText =
    variables["fetchGivingBodyOneTimeMessageText"];
  const fetchGivingBodyMonthlyMessageText =
    variables["fetchGivingBodyMonthlyMessageText"];

  const [amount1, setAmount1] = variables["amount1"];
  const [amount2, setAmount2] = variables["amount2"];
  const [amount3, setAmount3] = variables["amount3"];
  const [amount4, setAmount4] = variables["amount4"];

  const [showMonthlyDonationNudge, setShowMonthlyDonationNudge] =
    variables["showMonthlyDonationNudge"];
  const [monthlyDonationNudgeColor, setMonthlyDonationNudgeColor] =
    variables["monthlyDonationNudgeColor"];

  const [isMobileView, _] = variables["isMobileView"];

  const giftDesignationProjects = variables["giftDesignationProjects"] as Project[] || [];
  const giftDesignationLoading = variables["giftDesignationLoading"] as boolean;
  const addGiftDesignation = variables["addGiftDesignation"] as (name: string) => void;
  const removeGiftDesignation = variables["removeGiftDesignation"] as (index: number) => void;
  const updateGiftDesignationName = variables["updateGiftDesignationName"] as (index: number, name: string) => void;

  const [newProjectName, setNewProjectName] = useState("");

  const clickOneTime = variables["clickOneTime"];
  const clickMonthly = variables["clickMonthly"];

  const [selectedFrequency, setSelectedFrequency] = useState("one-time");

  const handleAddProject = () => {
    if (newProjectName.trim() && addGiftDesignation) {
      addGiftDesignation(newProjectName.trim());
      setNewProjectName("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddProject();
    }
  };

  useEffect(() => {
    if (!oneTimeMessageBody && fetchGivingBodyOneTimeMessageText) {
      fetchGivingBodyOneTimeMessageText();
    }
    if (!monthlyMessageBody && fetchGivingBodyMonthlyMessageText) {
      fetchGivingBodyMonthlyMessageText();
    }
  }, []);

  const applyPreviewForFrequency = (frequency: string) => {
    if (frequency === "one-time") {
      setOneTimeMessageBody(oneTimeMessageBody ?? "");
    } else {
      setMonthlyMessageBody(monthlyMessageBody ?? "");
    }

    if (headersColor) {
      setHeadersColor(headersColor);
    }
    if (inactiveTabColor) {
      setInactiveTabColor(inactiveTabColor);
    }

    if (amount1) {
      setAmount1(amount1);
    }
    if (amount2) {
      setAmount2(amount2);
    }
    if (amount3) {
      setAmount3(amount3);
    }
    if (amount4) {
      setAmount4(amount4);
    }
  };

  useEffect(() => {
    if (selectedFrequency === "one-time") {
      clickOneTime();
    } else {
      clickMonthly();
    }

    applyPreviewForFrequency(selectedFrequency);
  }, [selectedFrequency, clickOneTime, clickMonthly]);

  return (
    <CardBody>
      <div className={styles.section}>
        <SectionLabel
          label="Highlight Color"
          tooltip="Change the color of both the headers and the currently selected donation amount."
        />
        <TooltipColorPicker value={headersColor} setValue={setHeadersColor} />
        <SectionLabel
          label="Inactive Color"
          tooltip="Change the color of the currently inactive tab and amount buttons."
        />
        <TooltipColorPicker
          value={inactiveTabColor}
          setValue={setInactiveTabColor}
          includeAlphaValue
        />
      </div>

      {!isMobileView && (
        <div className={styles.section}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={!!showMonthlyDonationNudge}
                  onChange={e => setShowMonthlyDonationNudge(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Label className={styles.switchLabel}>
                  Show monthly donation nudge
                </Label>
              }
            />
          </FormGroup>
          {showMonthlyDonationNudge && (
            <>
              <SectionLabel
                label="Banner color"
                tooltip="Tint color for the monthly gift callout graphic on the donate form."
              />
              <div
                className={styles.graphicMessage}
                style={{ backgroundColor: monthlyDonationNudgeColor || "#00B4D8" }}
              />
              <TooltipColorPicker
                value={monthlyDonationNudgeColor}
                setValue={setMonthlyDonationNudgeColor}
              />
            </>
          )}
        </div>
      )}

      <div className={styles.section}>
        <SectionLabel
          label="Frequency"
          tooltip="Change the frequency you're currently updating."
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: 12,
            justifyContent: "center",
          }}
        >
          <ButtonGroup className="btn-group no-hover">
            <Button
              style={{
                backgroundColor:
                  selectedFrequency === "one-time" ? "#E2E8F0" : "#fff",
                fontSize: 14,
              }}
              className={styles.controlButton}
              onClick={() => setSelectedFrequency("one-time")}
            >
              One-time
            </Button>
            <Button
              style={{
                backgroundColor:
                  selectedFrequency === "monthly" ? "#E2E8F0" : "#fff",
                fontSize: 14,
              }}
              className={styles.controlButton}
              onClick={() => setSelectedFrequency("monthly")}
            >
              Monthly
            </Button>
          </ButtonGroup>
        </div>
        {selectedFrequency === "one-time" ? (
          <>
            <SectionLabel
              label="One-time Message"
              tooltip="Change the text that appears for the one-time donors."
            />
            <TextareaAutosize
              className={classNames(styles.textarea, styles.mainMessage)}
              placeholder="Here is a motivational text for one-time donations!"
              value={oneTimeMessageBody}
              onChange={e => setOneTimeMessageBody(e.target.value)}
              minRows={3}
              maxRows={12}
            />
          </>
        ) : (
          <>
            <SectionLabel
              label="Monthly Message"
              tooltip="Change the text that appears for the monthly donors."
            />
            <TextareaAutosize
              className={classNames(styles.textarea, styles.mainMessage)}
              placeholder="Here is a motivational text for monthly donations!"
              value={monthlyMessageBody}
              onChange={e => setMonthlyMessageBody(e.target.value)}
              minRows={3}
              maxRows={12}
            />
          </>
        )}

        <SectionLabel
          label="Donation Amounts"
          tooltip="Change the donation amount button values (same for both one-time and monthly)."
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          <div>
            <Label className={styles.label}>Amount 1</Label>
            <Input
              type="text"
              className={styles.input}
              placeholder="$25"
              value={amount1}
              onChange={e => setAmount1(e.target.value)}
            />
          </div>
          <div>
            <Label className={styles.label}>Amount 2</Label>
            <Input
              type="text"
              className={styles.input}
              placeholder="$50"
              value={amount2}
              onChange={e => setAmount2(e.target.value)}
            />
          </div>
          <div>
            <Label className={styles.label}>Amount 3</Label>
            <Input
              type="text"
              className={styles.input}
              placeholder="$100"
              value={amount3}
              onChange={e => setAmount3(e.target.value)}
            />
          </div>
          <div>
            <Label className={styles.label}>Amount 4</Label>
            <Input
              type="text"
              className={styles.input}
              placeholder="$200"
              value={amount4}
              onChange={e => setAmount4(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <SectionLabel
          label="Gift Designation Options"
          tooltip="Add or remove options from the gift designation dropdown. The first option is the default selection."
        />

        {giftDesignationLoading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : (
          <>
            <div style={{ marginTop: "12px" }}>
              <div className={styles.addProjectRow}>
                <input
                  type="text"
                  className={styles.addProjectInput}
                  placeholder="Enter designation name..."
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  type="button"
                  className={styles.addButton}
                  onClick={handleAddProject}
                  disabled={!newProjectName.trim()}
                >
                  Add
                </button>
              </div>
            </div>

            {giftDesignationProjects.length === 0 ? (
              <div className={styles.emptyState}>
                No gift designations configured
              </div>
            ) : (
              <div className={styles.designationList}>
                {giftDesignationProjects.map((project, index) => (
                  <div
                    key={project.id || index}
                    className={classNames(
                      styles.designationRow,
                      index === 0 && styles.isDefault
                    )}
                  >
                    <input
                      type="text"
                      className={styles.designationInput}
                      value={project.name}
                      onChange={(e) => updateGiftDesignationName(index, e.target.value)}
                    />
                    {index === 0 && (
                      <span className={styles.defaultBadge}>Default</span>
                    )}
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => removeGiftDesignation(index)}
                      title="Remove"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </CardBody>
  );
};

export default withStyles(styles)(GivingBodySection);
